import { AnimationEvent } from '@angular/animations';
import { ChangeDetectionStrategy, Component, HostBinding, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { AccaBoostConfig, CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { DeviceService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { ColumnLayoutService } from 'packages/sports/web/app/src/layout/column-layout.service';
import { Observable, combineLatest, debounceTime, filter, map, startWith, switchMap, tap, timer } from 'rxjs';

import { ACCA_BOOST_LAYOUT_PROVIDER, NotificationType } from '../../modules/acca-boost/model';
import { AccaBoostLayoutService } from '../../modules/acca-boost/services/digital/acca-boost-layout.service';
import { AccaBoostNotificationSyncService } from '../../modules/acca-boost/services/digital/acca-boost-notification-sync.service';
import { BetslipBarVisibilityService } from '../../modules/betslip-bar/services/betslip-bar-visibility.service';
import { selectIsQuickBetOpen } from '../../modules/quick-bet/quick-bet.selectors';
import { selectHasMinSelectionsBetBuilderError } from '../../modules/validation/selectors';
import { BetBuilderNotificationService } from '../services/bet-builder-notification.service';
import { BetslipReturnNotification, BetslipReturnsMessagesNotificationService } from '../services/betslip-returns-messages-notification.service';
import { notificationBubbleAnimation } from '../services/notification-bubble-animation';
import { RemindMeLaterService } from '../services/remind-me-later.service';

@Component({
    selector: 'bs-betslip-returns-messages',
    templateUrl: './betslip-returns-messages.component.html',
    styleUrls: ['./betslip-returns-messages.component.scss'],
    animations: [notificationBubbleAnimation],
    providers: [
        AccaBoostNotificationSyncService,
        BetslipReturnsMessagesNotificationService,
        { provide: ACCA_BOOST_LAYOUT_PROVIDER, useClass: AccaBoostLayoutService },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BetslipReturnsMessagesComponent {
    notificationTypeEnum = NotificationType;
    readonly usLadderEnabled: boolean;
    readonly isEnhancedLayoutEnabled: boolean;
    readonly notifications$: Observable<BetslipReturnNotification>;
    readonly betBuilderNotification$: Observable<string | null>;

    readonly isMobile = this.deviceService.isMobile;
    showRemindMe = false;
    isRemindMeLaterEnabled = this.tokensConfig.tokens.remindMeLaterNotificationEnabled;
    isRewardDrawerEnabled = this.tokensConfig.tokens.isRewardDrawerEnabled;
    MISSING_LEGS_COUNT_AFTER_ONE_IS_SELECTED = 1;
    NOTIFICATION_DISPLAY_TIME_IN_MILLISECONDS = 5000;
    wasMinComboBetDisplayedAlreadyOnCurrentTab = false;

    @HostBinding('class') className = 'betslip-returns-messages';

    isBetslipBarEnabled: Signal<boolean> = toSignal(this.betslipBarVisibilityService.enableDsl$, { initialValue: false });
    isQuickBetOpen = this.store.selectSignal(selectIsQuickBetOpen);

    constructor(
        public columnLayout: ColumnLayoutService,
        private accaBoostConfig: AccaBoostConfig,
        private tokensConfig: CrmConfig,
        private accaBoostNotificationSyncService: AccaBoostNotificationSyncService,
        private remindMeLaterService: RemindMeLaterService,
        notificationService: BetslipReturnsMessagesNotificationService,
        private betslipBarVisibilityService: BetslipBarVisibilityService,
        private store: Store,
        private sitecore: Sitecore,
        private betBuilderNotificationService: BetBuilderNotificationService,
        private deviceService: DeviceService,
    ) {
        this.usLadderEnabled = this.accaBoostConfig.isCompactLadderEnabled;
        this.isEnhancedLayoutEnabled = this.accaBoostConfig.isEnhancedLayoutEnabled;
        this.notifications$ = notificationService.notifications$.pipe(
            debounceTime(100),
            tap((model) => this.toggleBottomNav(model)),
        );

        const minSelectionErrorMessage = interpolateDoubleBracesString(this.sitecore.betBuilderEntainUI.messages?.QuickBetNonSufficientSelections, {
            count: this.MISSING_LEGS_COUNT_AFTER_ONE_IS_SELECTED,
        });

        const isBetBuilderTabActive$ = this.betBuilderNotificationService.isBetBuilderTabActive$.pipe(
            tap((isBetBuilderTabActivated) => {
                if (!isBetBuilderTabActivated) {
                    this.wasMinComboBetDisplayedAlreadyOnCurrentTab = false;
                }
            }),
        );

        this.betBuilderNotification$ = combineLatest([
            this.store.select(selectHasMinSelectionsBetBuilderError),
            this.store.select(selectIsQuickBetOpen),
            isBetBuilderTabActive$,
        ]).pipe(
            filter(([, isQuickBetOpen, isBetBuilderTabActive]) => !isQuickBetOpen && isBetBuilderTabActive),
            map(([minSelectionError]) => {
                if (minSelectionError) {
                    return minSelectionErrorMessage;
                } else if (!this.wasMinComboBetDisplayedAlreadyOnCurrentTab) {
                    this.wasMinComboBetDisplayedAlreadyOnCurrentTab = true;

                    return this.sitecore.betslip.validation.SelectMinComboBets;
                } else {
                    return null;
                }
            }),
            switchMap((message) =>
                timer(this.NOTIFICATION_DISPLAY_TIME_IN_MILLISECONDS).pipe(
                    map((_) => null),
                    startWith(message),
                ),
            ),
        );
    }

    onAnimationDone(event: AnimationEvent, type?: NotificationType): void {
        if (event.fromState === 'void' && type) {
            this.accaBoostNotificationSyncService.markAsDone(type);
        }
    }

    getMultiReturnAnimation(current: BetslipReturnNotification): 'bottom' | 'top' {
        return current.accaBoost === null ? 'bottom' : 'top';
    }

    private toggleBottomNav(model: BetslipReturnNotification): void {
        this.showRemindMe = !!model.tokensReminder;
    }

    resetReminder(): void {
        this.remindMeLaterService.showTokensReminder(false);
        this.showRemindMe = false;
    }
}
